/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    getPayloadObject,
    getTelemetryAttributes,
    IPayLoad,
    ITelemetryContent,
    KeyCodes,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { CustomInput } from 'reactstrap';

/**
 * Store Selector Search Form Props interface.
 */
export interface IStoreSelectorSearchFormProps {
    resources: {
        searchInputAriaLabel: string;
        searchButtonAriaLabel: string;
        searchPlaceholderText: string;
        viewListText: string;
        viewMapText: string;
        includeRetailersButtonLabel: string;
        pickupFilterByHeading?: string;
        pickupFilterMenuHeading?: string;
    };

    id: string;
    value: string;
    displayIncludeRetailersButton: boolean;
    isMapDisabled?: boolean;
    showAllStores?: boolean;
    displayRetailers?: boolean;
    displayList?: boolean;
    locatorView?: boolean;
    locationsMessage?: React.ReactNode;

    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;
    onShowAllStores(): void;
    onDisplayRetailers(displayRetailersValue: boolean): void;
    onToggleListMapViewState(): void;
    performSearch(searchTerm: string): Promise<void>;
    searchTermChanged(searchTerm: string): Promise<void>;
}

/**
 * Simple search form consisting of search text and a search button.
 */
export class StoreSelectorSearchForm extends React.PureComponent<IStoreSelectorSearchFormProps> {
    private readonly searchBoxRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();
    private readonly searchSectionRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    private readonly storeSearchAttributes: Msdyn365.IDictionary<string> | undefined;

    // private readonly showAllStoreAttributes: Msdyn365.IDictionary<string> | undefined;

    private readonly toggleMapViewAttributes: Msdyn365.IDictionary<string> | undefined;

    private previousValue: string = '';

    private isSearchResultOpened: boolean = false;

    public constructor(props: IStoreSelectorSearchFormProps) {
        super(props);
        this.state = { value: '' };
        // this._handleKeyPressPrev = this._handleKeyPressPrev.bind(this);
        const payLoad: IPayLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.SearchStore);
        this.storeSearchAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
        payLoad.contentAction.etext = TelemetryConstant.ShowAllStore;
        // this.showAllStoreAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
        payLoad.contentAction.etext = TelemetryConstant.ToggleMapView;
        this.toggleMapViewAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    }
    private setScrollPositionToSearch = () => {
        if (this.searchSectionRef.current?.offsetParent) {
            window.scrollTo({
                //@ts-ignore
                top: this.searchSectionRef.current.offsetParent.offsetTop - 48,
                behavior: 'auto'
            });
        }
    };

    public render(): JSX.Element {
        const {
            resources: { searchButtonAriaLabel, searchPlaceholderText, viewListText, viewMapText, includeRetailersButtonLabel },
            id,
            value,
            isMapDisabled,
            // showAllStores,
            displayList,
            displayRetailers,
            // onShowAllStores,
            onDisplayRetailers,
            locatorView,
            locationsMessage,
            displayIncludeRetailersButton
        } = this.props;

        const toggleButtonText = displayList ? viewMapText : viewListText;

        const onDisplayAllRetailersInternal = (event: React.ChangeEvent<HTMLInputElement>) => {
            const isChecked = event.target.checked;
            onDisplayRetailers(isChecked);
        };

        return (
            <div className='ms-store-select-venchi__search' ref={this.searchSectionRef}>
                <form
                    className='ms-store-select-venchi__search-form'
                    aria-label={searchButtonAriaLabel}
                    name='storeSelectorSearchForm'
                    autoComplete='off'
                    onSubmit={this._onSubmit}
                    id={`ms-store-select-venchi__search-box-container_${id}`}
                >
                    <input
                        type='text'
                        aria-label={this.props.resources.searchInputAriaLabel}
                        className='msc-form-control ms-store-select-venchi__search-input'
                        placeholder={searchPlaceholderText}
                        value={value}
                        onChange={this._searchTextChanged}
                        id={`ms-store-select-venchi__search-box_${id}`}
                        ref={this.searchBoxRef}
                        role='combobox'
                        aria-expanded='false'
                        aria-controls='as_containerSearch'
                        onKeyUp={this._handleInputKeyPress}
                    />
                    <button
                        className={`ms-store-select-venchi__search-button${isMapDisabled ? ' search-btn-disabled' : ''}`}
                        aria-label={searchButtonAriaLabel}
                        color='primary'
                        {...this.storeSearchAttributes}
                    />
                </form>
                <div className='ms-store-select-venchi__search-retailers-result-section'>
                    {displayIncludeRetailersButton ? (
                        <CustomInput
                            type='switch'
                            id='toggle-switch'
                            checked={displayRetailers}
                            onChange={onDisplayAllRetailersInternal}
                            label={includeRetailersButtonLabel}
                        />
                    ) : null}
                    {locationsMessage}
                    {this.renderToggleMapButton(this.props, toggleButtonText, locatorView, displayList)}
                </div>
            </div>
        );
    }

    /**
     * Handles the key press on the input box.
     * @param event - Event object.
     */
    private readonly _handleInputKeyPress = (event: React.KeyboardEvent): void => {
        // When the user navigates through the up and down arrow on the result returned by the auto suggest and press enter.
        // At that time, two requests were made to fetch the store details due to which incorrect result is shown and causes flickering.
        // In order to avoid that. Checking if the search result is open or not.
        if (event.keyCode === KeyCodes.ArrowUp || event.keyCode === KeyCodes.ArrowDown) {
            const activeDescedantValue = (event.target as HTMLInputElement).getAttribute('aria-activedescendant');
            if (activeDescedantValue) {
                if (this.previousValue === '') {
                    this.previousValue = activeDescedantValue;
                    this.isSearchResultOpened = true;
                    return;
                }

                if (activeDescedantValue !== this.previousValue) {
                    this.previousValue = activeDescedantValue;
                    this.isSearchResultOpened = true;
                } else {
                    this.isSearchResultOpened = false;
                }
            }
        }
    };

    // /**
    //  * Calls method display result.
    //  * @param event - The first number.
    //  */
    // private readonly _handleKeyPressPrev = (event: React.KeyboardEvent): void => {
    //     if (event.keyCode === KeyCodes.Enter || event.keyCode === KeyCodes.Space) {
    //         this.props.onShowAllStores();
    //     }
    // };

    /**
     * Method called on search text changed.
     * @param event - Input value.
     */
    private readonly _searchTextChanged = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const inputValue: string = event.target.value;

        await this.props.searchTermChanged(inputValue);
    };

    /**
     * Method called on search submit.
     * @param event - Input value.
     */
    private readonly _onSubmit = async (event: React.SyntheticEvent): Promise<void> => {
        event.preventDefault(); // Prevents form submission
        this.searchBoxRef.current?.blur();
        if (!this.isSearchResultOpened) {
            await this.props.performSearch(this.props.value);
        }
        this.isSearchResultOpened = false;
    };

    /**
     * Method to render pickup mode list.
     * @param props - Store selector search form props.
     * @param toggleButtonText - Locator view button text.
     * @param locatorView - Locator View is on\off.
     * @param shouldDisplayList - Display List.
     * @returns Jsx component.
     */
    private readonly renderToggleMapButton = (
        props: IStoreSelectorSearchFormProps,
        toggleButtonText: string,
        locatorView: boolean | undefined,
        shouldDisplayList?: boolean
    ): JSX.Element | null => {
        const handleButtonClick = () => {
            this.setScrollPositionToSearch();
            props.onToggleListMapViewState();
        };
        const toggleButtonClass = shouldDisplayList ? 'view-map' : 'list-view';
        if (locatorView) {
            return (
                <button
                    className={`ms-store-select-venchi__toggle-view ${toggleButtonClass}`}
                    onClick={handleButtonClick}
                    {...this.toggleMapViewAttributes}
                >
                    {toggleButtonText}
                </button>
            );
        }
        return null;
    };
}
